import { Spinner, Table } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { PaymentMethod } from '../../../constants/onlineStore';
import { formatCurrency, formatPercentage } from '../../../utils/strings';
import StyledBoxDelivery from './styles';
import getMaxAvailabilityDate from '../../../utils/getMaxAvailabilityDate';
import { useAuth } from '../../../contexts/authContext';

interface InvoiceSummaryProps {
    invoice: any;
    showInstallmentRow?: boolean;
    loading?: boolean;
}

export default function InvoiceSummary({
    invoice,
    showInstallmentRow = true,
    loading = false,
}: InvoiceSummaryProps) {
    const { user } = useAuth();
    const [pickupDate, setPickupDate] = useState<string | null>(null);
    const installmentPaymentMethods = [
        PaymentMethod.CreditCard,
        PaymentMethod.CreditCardAndBarcode,
    ];

    useEffect(() => {
        let mounted = true;

        async function loadPickupDate(): Promise<void> {
            if (invoice.addressType !== 'pickup') {
                if (mounted) setPickupDate(null);
                return;
            }

            try {
                const date = await getMaxAvailabilityDate(invoice);
                if (mounted) setPickupDate(date);
            } catch (err) {
                if (mounted) setPickupDate(null);
            }
        }

        loadPickupDate();

        return () => {
            mounted = false;
        };
    }, [invoice]);

    let noDeliveryMessage = 'Prazo de entrega indisponível';
    if (invoice.addressType === 'pickup' && pickupDate) 
        noDeliveryMessage = `Disponível para entrega em ${pickupDate}`;

    return (
        <>
            <h5 className="mt-4">
                <strong>Resumo do pedido</strong>
            </h5>
            <Table size="sm" className="mt-2">
                <tbody>
                    <tr>
                        <td className="text-right">
                            <strong>Sub Total: </strong>
                            {formatCurrency(
                                parseFloat(invoice.subTotal ?? '0'),
                            )}
                        </td>
                    </tr>

                    <tr>
                        <td className="text-right">
                            <strong>Desconto: </strong>
                            {loading ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                <>
                                    {formatCurrency(
                                        parseFloat(
                                            invoice.discountValue ?? '0',
                                        ),
                                    )}
                                    {' | '}
                                    {formatPercentage(
                                        invoice.percentage ?? '0',
                                    )}
                                </>
                            )}
                        </td>
                    </tr>

                    <tr>
                        <td className="text-right">
                            <strong>Total: </strong>
                            {loading ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                formatCurrency(
                                    parseFloat(
                                        invoice.subTotalWithDiscount ?? '0',
                                    ),
                                )
                            )}
                        </td>
                    </tr>

                    <tr>
                        <td className="text-right">
                            <strong>Acréscimos Cartão: </strong>
                            {loading ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                formatCurrency(
                                    parseFloat(invoice.interestValue ?? '0'),
                                )
                            )}
                        </td>
                    </tr>

                    <tr>
                        <td className="text-right">
                            <strong>Serviço Bruto: </strong>
                            {loading ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                <>
                                    {formatCurrency(
                                        parseFloat(
                                            invoice.grossCommission ?? '0',
                                        ),
                                    )}
                                    {' | '}
                                    <strong>Líquido: </strong>
                                    {formatCurrency(
                                        parseFloat(
                                            invoice.netCommission ?? '0',
                                        ),
                                    )}
                                </>
                            )}
                        </td>
                    </tr>
                    
                    { Number(invoice.engineeringInsuranceValue) > 0 &&
                        (
                            <tr>
                                <td className="text-right">
                                    <strong>Risco Engenharia: </strong>
                                    {loading ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : (
                                        <>
                                            {formatCurrency(
                                                parseFloat(
                                                    invoice.engineeringInsuranceValue ?? '0',
                                                ),
                                            )}
                                        </>
                                    )}
                                </td>
                            </tr>
                        )
                    }

                    {showInstallmentRow &&
                        installmentPaymentMethods.includes(
                            invoice.paymentMethod,
                        ) && (
                            <tr>
                                <td className="text-right">
                                    <strong>Parcelamento: </strong>
                                    {invoice.cardMaxInstallmentNumber}x
                                </td>
                            </tr>
                        )}

                    <tr>
                        <td className="text-right">
                            <strong>Total NF: </strong>
                            {loading ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                formatCurrency(
                                    parseFloat(invoice.totalInvoice ?? '0'),
                                )
                            )}
                        </td>
                    </tr>

                    {user.canViewMarginLiquid && !!invoice.marginLiquid && (
                        <tr>
                            <td className="text-right">
                                <strong>Margem líquida de produto: </strong>
                                {loading ? (
                                    <Spinner animation="border" size="sm" />
                                ) : (
                                    `${invoice.marginLiquid.toFixed(2)}%`
                                )}
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
            <StyledBoxDelivery className="text-center">
                <p>
                    <strong>
                        {invoice.deliveryDeadline &&
                        invoice.addressType === 'pickup'
                            ? `Previsão de entrega: ${invoice.deliveryDeadline} dias úteis`
                            : noDeliveryMessage}
                    </strong>
                </p>
            </StyledBoxDelivery>
        </>
    );
}
